import { digitsOnly } from './string';

export type Months =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12';

interface DayMonthYear {
  day: string | null;
  // 01 is January, 12 is December
  // Be careful when using this value with Date as that uses zero indexed months
  month: Months | string | null;
  year: string | null;
}

const daysInMonth = (month: Months | string, year: string) => {
  // Incrementing the month by one and then requesting day 0 gets the last day of the previous month.
  // note that month is zero indexed in Date so January is 0 and December is 11
  // but we use the date as values 01-12
  return new Date(Number(year), Number(month), 0).getDate();
};

export const dayIsValid = ({
  day: dayString,
  month,
  year,
}: Partial<DayMonthYear & Record<string, unknown>>) => {
  // fail if there are any non digits
  if (!dayString || !digitsOnly(dayString)) return false;

  // Do not run check if no values because we can’t work out how many days are valid
  // Instead this assumes that the required validator message will show.
  if (!month || !year || year.length < 4 || !digitsOnly(year)) return true;

  // If there are non digits in the year field, let the year validator handle it.
  // This is because we are unable to work out the exact number of days in a month without a valid year.
  const day = Number(dayString);

  return day > 0 && day < 32 && day <= daysInMonth(month, year);
};

/**
 * Validates a year is no earlier than 1865 and no later than the current year
 */
export const legacyYearInRange = (year: string | null) => {
  const yearNumber = Number(year);

  return yearNumber >= 1865 && yearNumber <= new Date().getFullYear();
};

/**
 * Creates a validator for a maximum date based on a minimumAge
 */
export const createMinimumAge = (minimumAge: number) => {
  const now = new Date();
  const maximumDate = new Date(
    now.getFullYear() - minimumAge,
    now.getMonth(),
    now.getDate(),
  );

  return ({ day, month, year }: Partial<DayMonthYear>) => {
    // Do not run check if no values because we can’t work out the date.
    // Instead this assumes that the required validator message will show.
    if (!month || !year || !day) return true;

    const dateOfBirth = new Date(Number(year), Number(month) - 1, Number(day));

    return dateOfBirth <= maximumDate;
  };
};
